import { Button, Modal } from '@mantine/core';
import { modals } from '@mantine/modals';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './ConfirmDelete.module.css';

export interface ConfirmDeleteOptions {
  /** Trigger text and modal title, e.g. "Delete form…". */
  label: string;
  /** Modal title when it should say more than the trigger, e.g. "Delete 1 message". */
  title?: string;
  description: ReactNode;
  confirmLabel: string;
  /** Reject to keep the dialog open; the API layer already reports the error. */
  onConfirm: () => Promise<void> | void;
}

export interface ConfirmDeleteProps extends ConfirmDeleteOptions {
  /** Trigger style: 'danger' is the outlined red button, 'subtle' the quiet row action. */
  variant?: 'danger' | 'subtle';
  size?: 'xs' | 'sm';
  disabled?: boolean;
}

const titleOf = (label: string) => label.replace(/…$/, '');

function ConfirmDeleteBody({
  description,
  confirmLabel,
  onConfirm,
  onClose,
}: Omit<ConfirmDeleteOptions, 'label' | 'title'> & { onClose: () => void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className={classes.body}>
      <div className={classes.description}>{description}</div>
      <div className={classes.buttons}>
        <Button onClick={onClose} disabled={busy}>
          {t('ui.cancel')}
        </Button>
        <Button
          variant="danger"
          className={classes.confirm}
          onClick={() => void confirm()}
          loading={busy}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/** For deletes started from somewhere other than a single trigger, e.g. a bulk bar. */
export function openConfirmDelete({ label, title, ...rest }: ConfirmDeleteOptions) {
  const id = modals.open({
    title: <span className={classes.title}>{title ?? titleOf(label)}</span>,
    children: <ConfirmDeleteBody {...rest} onClose={() => modals.close(id)} />,
  });
}

export function ConfirmDelete({
  label,
  title,
  description,
  confirmLabel,
  onConfirm,
  variant = 'danger',
  size = 'sm',
  disabled,
}: ConfirmDeleteProps) {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Button variant={variant} size={size} onClick={() => setOpened(true)} disabled={disabled}>
        {label}
      </Button>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={<span className={classes.title}>{title ?? titleOf(label)}</span>}
      >
        {opened && (
          <ConfirmDeleteBody
            description={description}
            confirmLabel={confirmLabel}
            onConfirm={onConfirm}
            onClose={() => setOpened(false)}
          />
        )}
      </Modal>
    </>
  );
}
